<template>
    <div class="animated fadeIn">
        <kgr-back-to-overview :club-id="clubId" />
        <div class="kgr-row kgr-postfix kgr-postfix--center kgr-postfix--gutters kgr-postfix--small-stacked">
            <div class="kgr-postfix__fluid">
                <h2 class="kgr-headline kgr-headline--h2">
                    {{ offering.title }} {{ offering.customId ? '/ ' + offering.customId : '' }}
                </h2>
            </div>
            <div class="kgr-postfix__fixed">
                <div class="kgr-postfix kgr-postfix--center kgr-postfix--smallGutters">
                    <div class="kgr-postfix__fixed">
                        <a
                            v-if="canDuplicate"
                            class="kgr-button kgr-button--accent"
                            style="margin-right: var(--kgr-input-padding-horizontal);"
                            :href="duplicateTrainingUrl"
                        >
                            <kgr-icon
                                icon-class="add"
                                small
                                circle
                                highlight
                                class="kgr-button__icon kgr-button__icon--left"
                            />
                            <span v-t="'offering.training.duplicate'"></span>
                        </a>
                    </div>
                    <div class="kgr-postfix__fixed">
                        <button
                            v-if="canEdit"
                            class="kgr-button kgr-button--accent"
                            @click="redirectToEdit(offering)"
                        >
                            <kgr-icon-edit />
                            {{ $t('offering.editOffering') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="kgr-row">
            {{ offering.description ? offering.description : '&nbsp;' }}
        </div>
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--large-1of4 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <div
                        class="kgr-definitionList kgr-region kgr-region--lightGray kgr-region--rounded"
                        style="height: 100%;"
                    >
                        <kgr-offering-image-view :offering="offering" />
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.sports') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ offering.sport ? offering.sport.name : '-' }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.offeringCategory') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ $t('offering.training') }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.bookable') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            <span style="white-space: pre;">{{ bookableString }} </span>
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('Abteilungen') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ departmentNames }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('Teams') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ teamNames }}
                        </div>
                    </div>
                </div>
                <div class="kgr-grid__cell">
                    <div
                        class="kgr-definitionList kgr-region kgr-region--lightGray kgr-region--rounded"
                        style="height: 100%;"
                    >
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.interval') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ offeringInterval }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.participants') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ $t('offering.min') }}:
                            {{ offering.minParticipants ? offering.minParticipants : '-' }} /
                            {{ $t('offering.max') }}:
                            {{ offering.maxParticipants ? offering.maxParticipants : '-' }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.age') }}:
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ $t('offering.min') }}: {{ offering.minimumAge ? offering.minimumAge : '-' }} /
                            {{ $t('offering.max') }}: {{ offering.maximumAge ? offering.maximumAge : '-' }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.gender') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ genders }}
                        </div>
                    </div>
                </div>
                <div class="kgr-grid__cell">
                    <div
                        class="kgr-definitionList kgr-region kgr-region--lightGray kgr-region--rounded"
                        style="height: 100%;"
                    >
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.locations') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ locationNamesForFields }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.instructors') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ instructorNames }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.requirements') }}:
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ offering.requirements ? offering.requirements : '-' }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.bookingMode') }}:
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ offering.bookingMode === 'offering' ? $t('offering.bookingModeOptions.offering') : $t('offering.bookingModeOptions.appointment') }}
                        </div>
                    </div>
                </div>
                <div class="kgr-grid__cell">
                    <div
                        class="kgr-definitionList kgr-region kgr-region--lightGray kgr-region--rounded"
                        style="height: 100%;"
                    >
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.prices') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ offeringPrices }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.registrationRequested') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ (offering.registrationRequested ? $t('offering.yes') : $t('offering.no')) }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.takesPlaceInSchoolHolidays') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ (offering.takesPlaceInSchoolHolidays ? $t('offering.yes') : $t('offering.no')) }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.takesPlaceOnPublicHoliday') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ (offering.takesPlaceOnPublicHoliday ? $t('offering.yes') : $t('offering.no')) }}
                        </div>
                        <div class="kgr-definitionList__term">
                            {{ $t('offering.executiontype') }}
                        </div>
                        <div class="kgr-definitionList__definition">
                            {{ $t('offering.' + offering.executionType) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="kgr-row">
            <h2 class="kgr-headline kgr-headline--h2">
                {{ $t('Termine') }}
            </h2>
        </div>
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--gutters ">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('von')"
                        :error="error"
                        field-name="bookingFrom"
                    >
                        <kgr-date-picker
                            v-model="bookingFrom"
                            @enter="reloadList"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('bis')"
                        :error="error"
                        field-name="bookingTo"
                    >
                        <kgr-date-picker
                            v-model="bookingTo"
                            @enter="reloadList"
                        />
                    </kgr-form-row>
                </div>
            </div>
        </div>
        <kgr-loader v-if="loading" />
        <div v-else>
            <kgr-empty-data
                v-if="appointments.length === 0"
                :value="$t('In dem gewählten Zeitraum gibt es keine Termine')"
            />
            <kgr-training-appointment-accordion
                v-else
                :appointments="appointments"
                :instructors="instructors"
                :offering="offering"
                :club-id="clubId"
                :user-id="userId"
                :booking-from="bookingFrom"
                :booking-to="bookingTo"
                :error="error"
            />
        </div>
    </div>
</template>

<script>
import AppointmentRepository from '../../js/repository/AppointmentRepository.js';
import BackToOverviewButton from './BackToOverviewButton.vue';
import DatePicker from '../common/form/picker/DatePicker.vue';
import EmptyData from '../../components/common/EmptyData.vue';
import Error from '../../js/model/Error.js';
import FormRow from '../common/form/FormRow.vue';
import IconEdit from '../../components/common/icon/IconEdit.vue';
import Instructors from '../../js/repository/InstructorRepository.js';
import KgrIcon from '../../components/common/icon/Icon.vue';
import Loading from '../../components/common/Loading.vue';
import Offering from '../../js/dto/offering/Offering.js'
import OfferingImageView from './OfferingImageView.vue';
import TrainingAppointmentAccordion from './appointment/TrainingAppointmentAccordion.vue';
import moment from 'moment';

export default {
    name: 'KgrOfferingTrainingView',
    components: {
        'kgr-back-to-overview': BackToOverviewButton,
        'kgr-date-picker': DatePicker,
        'kgr-empty-data': EmptyData,
        'kgr-form-row': FormRow,
        'kgr-icon': KgrIcon,
        'kgr-icon-edit': IconEdit,
        'kgr-loader': Loading,
        'kgr-offering-image-view': OfferingImageView,
        'kgr-training-appointment-accordion': TrainingAppointmentAccordion,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        duplicateTrainingUrl: {
            type: String,
            required: true,
        },
        canDuplicate: {
            type: Boolean,
            required: true,
        },
        canEdit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            bookingFrom: moment().format('DD.MM.YYYY'),
            bookingTo: moment().add(3, 'month').format('DD.MM.YYYY'),
            appointments: [],
            instructors: [],
        }
    },
    computed: {
        teamNames() {
            if (this.offering.teams.length === 0) {
                return '-';
            }
            return this.offering.teams.map(team => team.name).join(', ');
        },
        genders() {
            if (this.offering.bookableForWomen && this.offering.bookableForMen) {
                return this.$t('offering.mixed');
            } else if (this.offering.bookableForWomen) {
                return this.$t('offering.female');
            } else if (this.offering.bookableForMen) {
                return this.$t('offering.male');
            } else {
                return '-';
            }
        },
        locationNamesForFields() {
            const locationNames = [];
            if (this.offering.fields.length !== 0) {
                this.offering.fields.forEach(field => {
                    if (!locationNames.includes(field.name)) locationNames.push((field.name));
                });
            }
            if (locationNames.length === 0) {
                return '-';
            }
            return locationNames.join(', ');
        },
        bookableString() {
            let $resultString = '';
            if (this.offering.bookingType === 'perAppointment') {
                if (this.offering.bookingStartPerAppointmentAmount && this.offering.bookingStartPerAppointmentType) {
                    $resultString += this.$t('offering.bookingStart') + ' ' + this.offering.bookingStartPerAppointmentAmount + ' ' + this.$t('offeringBookingType.perAppointment.' + this.offering.bookingStartPerAppointmentType) + ' ' + this.$t('offeringBookingType.perAppointment.label');
                }
                if (this.offering.bookingStartPerAppointmentAmount && this.offering.bookingEndPerAppointmentAmount) {
                    $resultString += '\n'
                }
                if (this.offering.bookingEndPerAppointmentAmount && this.offering.bookingEndPerAppointmentType) {
                    $resultString += this.$t('offering.bookingEnd') + ' ' + this.offering.bookingEndPerAppointmentAmount + ' ' + this.$t('offeringBookingType.perAppointment.' + this.offering.bookingEndPerAppointmentType) + ' ' + this.$t('offeringBookingType.perAppointment.label');
                }
            } else {
                if (this.offering.start) {
                    $resultString += this.$t('offering.fromStart') + ' ' + moment(this.offering.start, 'YYYY-MM-DD').format('DD.MM.YYYY') + ' ';
                }
                if (this.offering.end) {
                    $resultString += this.$t('offering.toEnd') + ' ' + moment(this.offering.end, 'YYYY-MM-DD').format('DD.MM.YYYY');
                }
            }
            if ($resultString === '') {
                $resultString = '-';
            }
            return $resultString;
        },
        offeringPrices() {
            if (!this.offering.prices || this.offering.prices.length === 0) {
                return '-';
            }
            return this.offering.prices.map(price => this.getPriceDetails(price)).join(', ');
        },
        instructorNames() {
            if (this.offering.instructors.length === 0) {
                return '-';
            }
            return this.offering.instructors.map(instructor => instructor.fullName).join(', ');
        },
        offeringInterval() {
            if (this.offering.appointmentSeriesList) {
                return this.offering.appointmentSeriesList.map(
                    serie => serie.interval ?
                        this.$t('weekly_' + (serie.interval.weekday ? serie.interval.weekday : moment(serie.start, 'YYYY-MM-DD HH:mm').isoWeekday())) + ' (' + this.$t('interval.' + serie.interval.type) + ')' :
                        this.$t('weekday_' + moment(serie.start, 'YYYY-MM-DD HH:mm').isoWeekday()) + ', ' + this.formattedDateTime(serie.start)
                ).join(', ');
            } else {
                return '-';
            }
        },
        departmentNames() {
            if (this.offering.departments.length === 0) {
                return '-';
            }
            return this.offering.departments.map(department => department.name).join(', ');
        },
    },
    watch: {
        bookingFrom(newValue) {
            if (newValue) {
                this.reloadList();
            }
        },
        bookingTo(newValue) {
            if (newValue) {
                this.reloadList();
            }
        },
    },
    created() {
        this.getAppointments(this.offering.id, this.bookingFrom, this.bookingTo);
        this.getInstructors().then(result => {
            this.instructors.length = 0
            this.instructors.push(...result);
        });
    },
    methods: {
        getInstructors() {
            return Instructors.findAllByClub(this.clubId)
                .then(instructors => {
                    const result = [];
                    instructors.sort((a, b) => a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase()));
                    instructors.forEach(instructor => {
                        result.push({
                            'value': instructor.id,
                            'label': instructor.fullName
                        });
                    }
                    )
                    return result;
                });
        },
        getPriceDetails(price) {
            const clubFeeName = price.clubFee && price.clubFee.amount ? this.formatCurrency(price.clubFee.amount.numerator / price.clubFee.amount.denominator) : '-';
            return this.$t('offering.' + price.type) + ' (' + (
                price.divergentAmount ?
                    this.formatCurrency(price.divergentAmount.numerator / price.divergentAmount.denominator) :
                    clubFeeName
            )
                + ')'
        },
        formattedDateTime(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm');
        },
        convertDate(date) {
            return moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        },
        reloadList() {
            this.getAppointments(this.offering.id, this.bookingFrom, this.bookingTo);
        },
        getAppointments(offeringId, start, end) {
            this.loading = true;
            this.appointments = [];
            AppointmentRepository.findAll(offeringId, this.convertDate(start), this.convertDate(end))
                .then(appointments => {
                    this.appointments = appointments;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        redirectToEdit(offering) {
            window.location = `/backend/clubs/${this.clubId}/training-offerings/${offering.id}/edit`;
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
